import React from 'react';
import Layout from '../../components/layout/layout';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';
import HeroTemplateEnts from '../../components/sections/HeroTemplateEnts';
import { Button } from '../../components/ui/button';
import { motion } from 'framer-motion';


export default function IAEntreprisePage() {
  return (
    <Layout>
      <WebPageSchema
        page={{
          title: "IA RH",
          description: "Découvrez nos solutions d'IA RH concrètes qui automatisent vos tâches répétitives et accélèrent vos prises de décisions.",
          url: "/entreprise/ia"
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: '/' },
          { name: 'Entreprise', url: '/entreprise' },
          { name: 'IA', url: '/entreprise/ia' }
        ]}
      />

      <HeroTemplateEnts
        title="L'IA RH qui fait vraiment gagner du temps"
        description="Chez Recruteurs.io, on ne vous parle pas d'IA théorique. On conçoit des workflows concrets, sur mesure, pour automatiser vos tâches RH répétitives et accélérer vos prises de décisions."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Conseil-IA-marseille.webp"
        page="ia"
        breadcrumbs={[
          { name: 'Entreprise', href: '/entreprise' },
          { name: 'IA', href: '/entreprise/ia' }
        ]}
        cta1={{
          label: "Tester gratuitement",
          href: "/pimpmyjob"
        }}
        cta2={{
          label: "Parler à un expert RH",
          href: "/infos/contact"
        }}
      />
      
      {/* PimpMyJob */}
      <section className="py-24 bg-white">
        <div className="container-custom grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <div>
              <h2 className="text-3xl font-bold mb-6">🎯 Générez des offres impactantes</h2>
              <p className="text-lg text-neutral-600 mb-6">
                Avec <strong>PimpMyJob</strong>, vous générez en quelques secondes des offres d'emploi personnalisées, cohérentes avec votre culture, optimisées SEO, et pensées pour générer de l'engagement.
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6">
                <li>✍️ Ton éditorial fidèle à votre style</li>
                <li>⚡️ 80% de gain de temps en rédaction</li>
                <li>📈 +45% de candidatures qualifiées observées (source : bêta tests 2024)</li>
              </ul>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Button variant="gradient" href="/pimpmyjob">Tester l'outil</Button>
                <span className="text-green-600 font-medium">✅ Gratuit pour les clients recruteurs.io</span>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/offreemploiia.svg" alt="PimpMyJob UI" className="w-full h-full object-contain bg-transparent" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Atterrissage RH */}
      <section className="py-24 bg-neutral-50">
        <div className="container-custom grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/MappingRH.svg" alt="Atterrissage RH" className="w-full h-full object-cover" />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <div>
              <h2 className="text-3xl font-bold mb-6">🧠 Mesurez l'alignement d'un candidat à votre équipe</h2>
              <p className="text-lg text-neutral-600 mb-6">
                Notre outil <strong>Atterrissage RH</strong> évalue en 5 minutes la compatibilité d'un candidat avec vos valeurs, votre style de management, et les personnalités de l'équipe. Vous gagnez en justesse dans vos décisions d'embauche.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Button variant="gradient" href="/atterrissage-rh">Découvrir</Button>
                <span className="text-green-600 font-medium">✅ Gratuit pour les clients recruteurs.io</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Onboarding IA */}
      <section className="py-24 bg-white">
        <div className="container-custom grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <div>
              <h2 className="text-3xl font-bold mb-6">🤝 Automatisez l'intégration</h2>
              <p className="text-lg text-neutral-600 mb-6">
                Notre système d'onboarding intelligent envoie les bons messages au bon moment, déclenche des parcours de formation et alerte les managers sur les points clés. Résultat : une intégration sans friction et des collaborateurs plus vite opérationnels.
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6">
                <li>🎓 Capsules de formation IA</li>
                <li>🔔 Notifications managériales automatisées</li>
                <li>📅 Parcours personnalisé</li>
              </ul>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Button variant="gradient" href="/onboarding">En savoir plus</Button>
                <span className="text-green-600 font-medium">✅ Gratuit pour les clients recruteurs.io</span>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/onboarding-_1_.webp" alt="Onboarding RH" className="w-full h-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-r from-[#d03484] to-[#d47254] text-white text-center">
        <div className="container-custom max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">💡 Prêt à pimper vos processus RH avec l'IA ?</h2>
          <p className="text-lg md:text-xl mb-8">
            Nous concevons des outils sur mesure, adapatés à vos processus métiers. Nos outils sont conçus pour les équipes RH ambitieuses mais débordées. Un setup simple, un gain de temps énorme.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button variant="gradient" size="lg" href="/offres">
              Voir nos offres RH
            </Button>
            <Button variant="outline" size="lg" href="/audit" className="text-white border-white hover:bg-white/10">
              Demander un audit IA de mes processus métiers
            </Button>
          </div>
        </div>
      </section>
      <section className="bg-white py-16 px-6 md:px-16">
  <div className="max-w-4xl mx-auto text-lg text-neutral-800 leading-relaxed">
    <h2 className="text-3xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
      IA RH : une révolution concrète pour les équipes débordées
    </h2>
    <p className="mb-4">
      Chez Recruteurs.io, on parle d’<strong>IA RH</strong> concrète, accessible, et orientée action. Depuis Marseille, notre équipe conçoit des outils intelligents pensés pour fluidifier vos processus de recrutement, d’intégration et de pilotage RH. Oubliez les promesses floues : ici, chaque outil est testé, éprouvé, et améliore réellement votre quotidien.
    </p>
    <p className="mb-4">
      Grâce à notre plateforme <a href="https://pimpmyjob.fr" className="underline">PimpMyJob</a>, vous générez des offres d’emploi optimisées SEO, alignées sur votre culture, en quelques clics. Avec notre module <a href="https://recruteurs.io/ressources/mapping-rh" className="underline">Atterrissage RH</a>, vous évaluez la compatibilité humaine d’un candidat avec votre équipe. Et pour les nouvelles recrues, notre système <a href="https://recruteurs.io/ressources/outils" className="underline">d'onboarding IA</a> fluidifie l'intégration dès le premier jour.
    </p>
    <p className="mb-4">
      Vous souhaitez aller plus loin ? Nous réalisons un <a href="/audit" className="underline">audit IA complet de vos processus métiers RH</a> pour identifier les étapes automatisables, les goulots d’étranglement et les points de friction. L’objectif : vous libérer du temps pour vous concentrer sur ce qui compte vraiment.
    </p>
    <p>
      Nos <a href="/offres" className="underline">offres RH</a> sont simples, claires et adaptées à vos besoins. Vous êtes une PME, une scale-up ou une DRH solo ? On parle votre langue. Avec Recruteurs.io, l’IA devient un levier RH puissant, pas un gadget marketing.
    </p>
  </div>
</section>
    </Layout>
  );
}